import { useState, useEffect, useCallback, useRef } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { Calendar, Clock, MapPin, Truck, CheckCircle2, Loader2, MessageSquare, ArrowRight, RefreshCw, AlertCircle, Search } from "lucide-react";
import { Button } from "../components/ui/button";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import { toast } from "sonner";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const GMAPS_KEY = process.env.REACT_APP_GOOGLE_MAPS_API_KEY || "";

function formatTime(t) {
  if (!t) return "";
  const [h, m] = t.split(":");
  const hr = parseInt(h);
  const ampm = hr >= 12 ? "PM" : "AM";
  const hr12 = hr === 0 ? 12 : hr > 12 ? hr - 12 : hr;
  return `${hr12}:${m} ${ampm}`;
}

// ─── Google Maps loader (idempotent, only loads Places once) ──────────────
let __gmapsPromise = null;
function loadGoogleMaps() {
  if (!GMAPS_KEY) return Promise.resolve(null);
  if (typeof window === "undefined") return Promise.resolve(null);
  if (window.google?.maps?.places) return Promise.resolve(window.google);
  if (__gmapsPromise) return __gmapsPromise;
  __gmapsPromise = new Promise((resolve, reject) => {
    const script = document.createElement("script");
    script.src = `https://maps.googleapis.com/maps/api/js?key=${GMAPS_KEY}&libraries=places&v=weekly`;
    script.async = true;
    script.defer = true;
    script.onload = () => resolve(window.google);
    script.onerror = () => { __gmapsPromise = null; reject(new Error("Failed to load Google Maps")); };
    document.head.appendChild(script);
  });
  return __gmapsPromise;
}

// ─── Address input with Places Autocomplete + manual geocoding fallback ──
function DeliveryAddressInput({ value, onChange, validation, onValidate, validating }) {
  const inputRef = useRef(null);
  const autoRef = useRef(null);
  const [ready, setReady] = useState(false);

  useEffect(() => {
    let active = true;
    loadGoogleMaps().then((g) => {
      if (!active || !g || !inputRef.current) return;
      try {
        autoRef.current = new g.maps.places.Autocomplete(inputRef.current, {
          componentRestrictions: { country: "au" },
          fields: ["formatted_address", "geometry", "place_id"],
          types: ["address"],
        });
        autoRef.current.addListener("place_changed", () => {
          const p = autoRef.current.getPlace();
          if (!p?.geometry?.location) return;
          const addr = {
            address: p.formatted_address || "",
            lat: p.geometry.location.lat(),
            lng: p.geometry.location.lng(),
            place_id: p.place_id || "",
            validation_method: "autocomplete",
          };
          onChange(addr);
          onValidate(addr);
        });
        setReady(true);
      } catch { /* silent */ }
    }).catch(() => { /* fallback to manual */ });
    return () => { active = false; };
  }, [onChange, onValidate]);

  const submitManual = async () => {
    const text = inputRef.current?.value?.trim() || value?.address?.trim();
    if (!text) { toast.error("Enter your delivery address"); return; }
    try {
      const res = await axios.post(`${API}/booking/geocode`, { address: text });
      const addr = {
        address: res.data.formatted_address,
        lat: res.data.lat, lng: res.data.lng,
        place_id: res.data.place_id || "",
        validation_method: res.data.validation_method || "manual_geocoded",
      };
      onChange(addr);
      onValidate(addr);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not find this address");
    }
  };

  return (
    <div className="bg-white border border-[#282828]/8 p-5" data-testid="delivery-address-block">
      <label className="text-[10px] uppercase tracking-wider text-[#676664]/50 block mb-2">
        Delivery address
      </label>
      <div className="flex items-stretch gap-2">
        <div className="relative flex-1">
          <MapPin className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-[#9A7B4F]/60" strokeWidth={1.5} />
          <input
            ref={inputRef}
            type="text"
            defaultValue={value?.address || ""}
            onChange={(e) => onChange({ ...(value || {}), address: e.target.value, lat: null, lng: null, validation_method: "" })}
            onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); submitManual(); } }}
            placeholder="Start typing your address…"
            className="w-full pl-9 pr-3 py-2.5 border border-[#282828]/12 text-[13px] bg-white focus:border-[#9A7B4F] focus:outline-none"
            data-testid="delivery-address-input"
            autoComplete="off"
          />
        </div>
        <button
          type="button"
          onClick={submitManual}
          disabled={validating}
          className="px-3 py-2.5 border border-[#282828]/12 text-[11px] tracking-wider uppercase text-[#9A7B4F] hover:bg-[#9A7B4F]/5 disabled:opacity-50"
          data-testid="delivery-address-check-btn"
          title="Validate this address"
        >
          {validating ? <Loader2 className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />}
        </button>
      </div>

      <p className="text-[10px] text-[#676664]/50 mt-1.5">
        {GMAPS_KEY && ready ? "Powered by Google. Select a suggestion or press the check button." : "Enter your address and press the check button."}
      </p>

      {validation && (
        <div className={`mt-3 flex items-start gap-2 text-[12px] p-3 ${validation.valid ? "bg-emerald-50 text-emerald-700" : "bg-amber-50 text-amber-800"}`} data-testid="delivery-validation-result">
          {validation.valid ? <CheckCircle2 className="w-4 h-4 mt-0.5" /> : <AlertCircle className="w-4 h-4 mt-0.5" />}
          <div>
            <p className="font-medium">{validation.valid ? "Within delivery range" : "Out of delivery range"}</p>
            <p className="opacity-80">
              {validation.distance_km} km from us · radius {validation.radius_km} km
            </p>
            {!validation.valid && (
              <p className="mt-1">{validation.message || "Pick-up is available — switch above."}</p>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Suggestions panel (rendered on conflict) ─────────────────────────────
function SuggestionsBlock({ suggestions, onPick }) {
  if (!suggestions || suggestions.length === 0) return null;
  return (
    <div className="mt-4 p-4 bg-amber-50 border border-amber-200" data-testid="suggestions-block">
      <p className="text-[12px] text-amber-800 font-medium mb-2">
        That time is no longer available. Try one of these instead:
      </p>
      <div className="flex flex-wrap gap-2">
        {suggestions.map((t) => (
          <button
            key={t}
            onClick={() => onPick(t)}
            className="px-3 py-1.5 text-[12px] bg-white border border-amber-300 hover:border-amber-500 hover:bg-amber-100 text-amber-900"
            data-testid={`suggestion-${t}`}
          >
            {formatTime(t)}
          </button>
        ))}
      </div>
    </div>
  );
}


export default function AppointmentBooking() {
  const [searchParams] = useSearchParams();
  const cartGroupId = searchParams.get("cart_group") || "";
  const bookingId = searchParams.get("booking_id") || "";

  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pickupSlots, setPickupSlots] = useState([]);
  const [dropoffSlots, setDropoffSlots] = useState([]);
  const [selectedPickup, setSelectedPickup] = useState({ date: "", time: "" });
  const [selectedDropoff, setSelectedDropoff] = useState({ date: "", time: "" });
  const [submitting, setSubmitting] = useState(false);
  const [bookedAppointments, setBookedAppointments] = useState([]);
  const [step, setStep] = useState("pickup"); // pickup, dropoff, done
  const [showRequestForm, setShowRequestForm] = useState(null);
  const [requestMessage, setRequestMessage] = useState("");
  const [requestTime, setRequestTime] = useState("");
  const [requestDate, setRequestDate] = useState("");
  const [requestDateError, setRequestDateError] = useState("");
  const PAST_DATE_MSG = "Please select today’s date or a future date.";
  const handleReqDateInvalid = (e) => { e.preventDefault(); e.target.setCustomValidity(PAST_DATE_MSG); setRequestDateError(PAST_DATE_MSG); };
  const handleReqDateInput = (e) => { e.target.setCustomValidity(""); setRequestDateError(""); };
  const [customerInfo, setCustomerInfo] = useState({ name: "", email: "", phone: "" });
  const [conflictSuggestions, setConflictSuggestions] = useState({ pickup: [], dropoff: [] });

  // Delivery state (only when isDelivery=true)
  const [deliveryAddress, setDeliveryAddress] = useState(null); // { address, lat, lng, place_id, validation_method }
  const [deliveryValidation, setDeliveryValidation] = useState(null);
  const [validatingAddress, setValidatingAddress] = useState(false);
  const [forcePickupMode, setForcePickupMode] = useState(false);

  // Fetch booking details
  useEffect(() => {
    (async () => {
      try {
        if (cartGroupId) {
          const res = await axios.get(`${API}/bookings/by-group/${cartGroupId}`);
          const bs = res.data?.bookings || [];
          setBookings(Array.isArray(bs) ? bs : []);
          if (bs.length > 0) setCustomerInfo({ name: bs[0].customer_name || "", email: bs[0].customer_email || "", phone: bs[0].customer_phone || "" });
        } else if (bookingId) {
          const res = await axios.get(`${API}/bookings/${bookingId}`);
          setBookings([res.data]);
          setCustomerInfo({ name: res.data.customer_name || "", email: res.data.customer_email || "", phone: res.data.customer_phone || "" });
        }
      } catch { /* silent */ }
      setLoading(false);
    })();
  }, [cartGroupId, bookingId]);

  const mainBooking = bookings.find(b => !b.is_addon) || bookings[0];
  const isDelivery = mainBooking?.delivery_method === "delivery" && !forcePickupMode;
  const appointmentTypeForStep = (() => {
    if (step === "pickup") return isDelivery ? "delivery" : "pickup";
    return "dropoff";
  })();

  // Fetch available slots
  useEffect(() => {
    if (bookings.length === 0) return;
    const eventDate = mainBooking?.event_date;
    if (!eventDate) return;
    const pickupType = isDelivery ? "delivery" : "pickup";
    const fetchSlots = async () => {
      try {
        const [pickupRes, dropoffRes] = await Promise.all([
          axios.get(`${API}/appointments/available-slots?appointment_type=${pickupType}&event_date=${eventDate}`),
          axios.get(`${API}/appointments/available-slots?appointment_type=dropoff&event_date=${eventDate}`),
        ]);
        setPickupSlots(pickupRes.data.dates || []);
        setDropoffSlots(dropoffRes.data.dates || []);
      } catch { /* silent */ }
    };
    fetchSlots();
  }, [bookings, mainBooking, isDelivery]);

  // Check if already has appointments
  useEffect(() => {
    if (!cartGroupId && !bookingId) return;
    (async () => {
      try {
        const params = cartGroupId ? `cart_group_id=${cartGroupId}` : `booking_id=${bookingId}`;
        const res = await axios.get(`${API}/appointments/my?${params}`);
        const appts = res.data.appointments || [];
        setBookedAppointments(appts);
        const hasFirst = appts.find(a => ["pickup", "delivery"].includes(a.appointment_type));
        const hasDrop = appts.find(a => a.appointment_type === "dropoff");
        if (hasFirst && hasDrop) setStep("done");
        else if (hasFirst) setStep("dropoff");
      } catch { /* silent */ }
    })();
  }, [cartGroupId, bookingId]);

  const validateAddress = useCallback(async (addr) => {
    if (!addr?.lat || !addr?.lng) return;
    setValidatingAddress(true);
    try {
      const res = await axios.post(`${API}/booking/validate-delivery`, { lat: addr.lat, lng: addr.lng });
      setDeliveryValidation(res.data);
    } catch {
      toast.error("Could not validate delivery address");
    } finally {
      setValidatingAddress(false);
    }
  }, []);

  const bookAppointment = useCallback(async (typeKey) => {
    const sel = typeKey === "dropoff" ? selectedDropoff : selectedPickup;
    if (!sel.date || !sel.time) {
      toast.error("Please select a date and time");
      return;
    }
    const isPickupStep = typeKey !== "dropoff";
    const appointmentType = isPickupStep ? (isDelivery ? "delivery" : "pickup") : "dropoff";

    if (appointmentType === "delivery") {
      if (!deliveryAddress?.lat || !deliveryAddress?.lng) {
        toast.error("Please enter and validate your delivery address");
        return;
      }
      if (deliveryValidation && !deliveryValidation.valid) {
        toast.error("Delivery is out of range — please switch to pick-up");
        return;
      }
    }

    setSubmitting(true);
    setConflictSuggestions((prev) => ({ ...prev, [isPickupStep ? "pickup" : "dropoff"]: [] }));
    try {
      const res = await axios.post(`${API}/appointments/book`, {
        booking_ids: bookings.map(b => b.id),
        cart_group_id: cartGroupId,
        appointment_type: appointmentType,
        date: sel.date,
        time: sel.time,
        customer_name: customerInfo.name,
        customer_email: customerInfo.email,
        customer_phone: customerInfo.phone,
        delivery_address: appointmentType === "delivery" ? deliveryAddress?.address || "" : "",
        delivery_lat: appointmentType === "delivery" ? deliveryAddress?.lat : null,
        delivery_lng: appointmentType === "delivery" ? deliveryAddress?.lng : null,
        delivery_place_id: appointmentType === "delivery" ? deliveryAddress?.place_id || "" : "",
        validation_method: appointmentType === "delivery" ? deliveryAddress?.validation_method || "" : "",
      });
      setBookedAppointments(prev => [...prev, res.data]);
      toast.success(`${isPickupStep ? (isDelivery ? "Delivery" : "Pick-up") : "Drop-off"} appointment booked!`);
      if (isPickupStep) setStep("dropoff"); else setStep("done");
    } catch (err) {
      const detail = err.response?.data?.detail;
      if (detail && typeof detail === "object" && Array.isArray(detail.suggestions)) {
        setConflictSuggestions((prev) => ({ ...prev, [isPickupStep ? "pickup" : "dropoff"]: detail.suggestions }));
        toast.error(detail.message || "Time conflict — see suggestions below");
      } else {
        toast.error(typeof detail === "string" ? detail : "Failed to book appointment");
      }
    } finally {
      setSubmitting(false);
    }
  }, [selectedPickup, selectedDropoff, bookings, cartGroupId, customerInfo, isDelivery, deliveryAddress, deliveryValidation]);

  const submitRequest = async (typeKey) => {
    if (!requestDate || !requestTime) { toast.error("Please enter your preferred date and time"); return; }
    {
      const today = new Date(); today.setHours(0, 0, 0, 0);
      const picked = new Date(requestDate + "T00:00:00");
      if (picked < today) { setRequestDateError(PAST_DATE_MSG); toast.error(PAST_DATE_MSG); return; }
    }
    setRequestDateError("");
    setSubmitting(true);
    try {
      const isPickupStep = typeKey !== "dropoff";
      const appointmentType = isPickupStep ? (isDelivery ? "delivery" : "pickup") : "dropoff";
      await axios.post(`${API}/appointments/request`, {
        booking_ids: bookings.map(b => b.id),
        cart_group_id: cartGroupId,
        appointment_type: appointmentType,
        requested_date: requestDate,
        requested_time: requestTime,
        customer_name: customerInfo.name,
        customer_email: customerInfo.email,
        customer_phone: customerInfo.phone,
        message: requestMessage,
      });
      toast.success("Your request has been sent!");
      setShowRequestForm(null);
      setRequestMessage(""); setRequestTime(""); setRequestDate("");
      if (isPickupStep) setStep("dropoff"); else setStep("done");
    } catch {
      toast.error("Failed to submit request");
    } finally {
      setSubmitting(false);
    }
  };

  const getLabel = (type) => {
    if (type !== "dropoff") return isDelivery ? "Delivery" : "Pick-up";
    return isDelivery ? "Return Pick-up" : "Drop-off";
  };
  const getDescription = (type) => {
    if (type !== "dropoff") return isDelivery
      ? "Select a time for us to deliver your outfit the day before your event"
      : "Select a time to collect your outfit the day before your event";
    return isDelivery
      ? "Select a time for us to pick up your outfit after your event"
      : "Select a time to return your outfit after your event";
  };

  if (loading) return (
    <div className="min-h-screen bg-[#FAF8F0]">
      <Navbar />
      <div className="flex items-center justify-center py-32"><Loader2 className="w-8 h-8 animate-spin text-[#9A7B4F]" /></div>
    </div>
  );

  if (step === "done") {
    const firstAppt = bookedAppointments.find(a => ["pickup", "delivery"].includes(a.appointment_type));
    const dropoffAppt = bookedAppointments.find(a => a.appointment_type === "dropoff");
    return (
      <div className="min-h-screen bg-[#FAF8F0] flex flex-col">
        <Navbar />
        <div className="flex-1 max-w-[640px] mx-auto px-4 py-12 w-full">
          <div className="text-center mb-10">
            <div className="w-16 h-16 bg-[#D4B8B4] flex items-center justify-center mx-auto mb-5">
              <CheckCircle2 className="w-8 h-8 text-white" strokeWidth={1.5} />
            </div>
            <p className="text-[10px] tracking-[0.25em] uppercase text-[#9A7B4F] mb-3">All Set</p>
            <h1 className="text-2xl lg:text-3xl text-[#1A1A1A]" style={{ fontFamily: "'Cormorant Garamond', serif", fontWeight: 500 }}>
              Appointments Confirmed
            </h1>
            <p className="text-[13px] text-[#676664]/60 mt-2">You&apos;re all booked in. We look forward to seeing you!</p>
          </div>

          <div className="space-y-4">
            {firstAppt && (
              <AppointmentCard appointment={firstAppt} type={firstAppt.appointment_type} label={getLabel(firstAppt.appointment_type)} onReschedule={() => setStep("pickup")} />
            )}
            {dropoffAppt && (
              <AppointmentCard appointment={dropoffAppt} type="dropoff" label={getLabel("dropoff")} onReschedule={() => setStep("dropoff")} />
            )}
          </div>

          <p className="text-center text-[11px] text-[#676664]/50 mt-6">
            {isDelivery ? "We'll text you to confirm before delivery" : "Address will be sent via text message the day before"}
          </p>

          <div className="flex flex-col sm:flex-row gap-3 justify-center mt-8">
            <Link to="/catalog">
              <Button className="bg-[#9A7B4F] hover:bg-[#8B6B3F] text-white px-8 py-4 text-[11px] tracking-[0.12em] uppercase" data-testid="continue-browsing-btn">
                Continue Browsing <ArrowRight className="ml-2 w-4 h-4" />
              </Button>
            </Link>
            <Link to="/">
              <Button variant="outline" className="border-[#282828]/15 text-[#676664] px-8 py-4 text-[11px] tracking-[0.12em] uppercase" data-testid="back-home-btn">
                Back to Home
              </Button>
            </Link>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  const isPickupStep = step === "pickup";
  const currentSlots = isPickupStep ? pickupSlots : dropoffSlots;
  const currentSelection = isPickupStep ? selectedPickup : selectedDropoff;
  const setCurrentSelection = isPickupStep ? setSelectedPickup : setSelectedDropoff;
  const typeLabel = getLabel(isPickupStep ? "pickup" : "dropoff");
  const typeKey = isPickupStep ? "pickup" : "dropoff";
  const stepSuggestions = conflictSuggestions[typeKey] || [];

  const deliveryBlocksConfirm = isPickupStep && isDelivery && (!deliveryAddress?.lat || (deliveryValidation && !deliveryValidation.valid));

  return (
    <div className="min-h-screen bg-[#FAF8F0] flex flex-col">
      <Navbar />
      <div className="flex-1 max-w-[640px] mx-auto px-4 py-10 w-full">
        <div className="text-center mb-8">
          <p className="text-[10px] tracking-[0.25em] uppercase text-[#9A7B4F] mb-3">
            {isPickupStep ? "Step 1 of 2" : "Step 2 of 2"}
          </p>
          <h1 className="text-2xl lg:text-3xl text-[#1A1A1A] mb-2" style={{ fontFamily: "'Cormorant Garamond', serif", fontWeight: 500 }}>
            Book Your {typeLabel}
          </h1>
          <p className="text-[13px] text-[#676664]/60">{getDescription(typeKey)}</p>
        </div>

        {mainBooking && (
          <div className="bg-white border border-[#282828]/8 p-4 mb-6" data-testid="booking-reference">
            <div className="flex items-center gap-3">
              {mainBooking.outfit_image && (
                <div className="w-14 h-[70px] bg-[#f5f3f0] overflow-hidden flex-shrink-0">
                  <img src={mainBooking.outfit_image} alt="" className="w-full h-full object-cover" />
                </div>
              )}
              <div className="min-w-0">
                <p className="text-[14px] text-[#1A1A1A] truncate" style={{ fontFamily: "'Cormorant Garamond', serif", fontWeight: 600 }}>{mainBooking.outfit_name}</p>
                <p className="text-[11px] text-[#676664]/60">Event: {new Date(mainBooking.event_date).toLocaleDateString("en-AU", { weekday: "short", day: "numeric", month: "short", year: "numeric" })}</p>
              </div>
            </div>
          </div>
        )}

        {/* Delivery address block (only on first step + delivery mode) */}
        {isPickupStep && isDelivery && (
          <div className="mb-6">
            <DeliveryAddressInput
              value={deliveryAddress}
              onChange={setDeliveryAddress}
              onValidate={validateAddress}
              validating={validatingAddress}
              validation={deliveryValidation}
            />
            {deliveryValidation && !deliveryValidation.valid && (
              <button
                onClick={() => { setForcePickupMode(true); setDeliveryAddress(null); setDeliveryValidation(null); }}
                className="w-full mt-3 px-4 py-2.5 bg-[#9A7B4F] text-white text-[11px] tracking-wider uppercase hover:bg-[#8B6B3F]"
                data-testid="switch-to-pickup-btn"
              >
                Switch to Pick-up Instead
              </button>
            )}
          </div>
        )}

        {/* Slot selection */}
        {showRequestForm === typeKey ? (
          <div className="bg-white border border-[#282828]/8 p-5" data-testid="custom-request-form">
            <h3 className="text-[14px] text-[#282828] mb-4" style={{ fontFamily: "'Cormorant Garamond', serif", fontWeight: 600 }}>
              Request a Custom Time
            </h3>
            <div className="space-y-3">
              <div>
                <label className="text-[10px] uppercase tracking-wider text-[#676664]/50 block mb-1">Preferred Date</label>
                <input type="date" value={requestDate} onChange={e => { setRequestDate(e.target.value); handleReqDateInput(e); }}
                  onInvalid={handleReqDateInvalid}
                  onInput={handleReqDateInput}
                  min={new Date().toISOString().slice(0, 10)}
                  className={`w-full border px-3 py-2.5 text-[13px] bg-white focus:outline-none ${requestDateError ? "border-[#B85C5C] focus:border-[#B85C5C]" : "border-[#282828]/12 focus:border-[#9A7B4F]"}`}
                  data-testid="request-date-input"
                  aria-invalid={!!requestDateError}
                  aria-describedby={requestDateError ? "request-date-error" : undefined}
                />
                {requestDateError && (
                  <p id="request-date-error" role="alert" className="mt-1 text-[11px] text-[#B85C5C] leading-snug" data-testid="request-date-error">
                    {requestDateError}
                  </p>
                )}
              </div>
              <div>
                <label className="text-[10px] uppercase tracking-wider text-[#676664]/50 block mb-1">Preferred Time</label>
                <input type="time" value={requestTime} onChange={e => setRequestTime(e.target.value)}
                  className="w-full border border-[#282828]/12 px-3 py-2.5 text-[13px] bg-white focus:border-[#9A7B4F] focus:outline-none" data-testid="request-time-input" />
              </div>
              <div>
                <label className="text-[10px] uppercase tracking-wider text-[#676664]/50 block mb-1">Message (optional)</label>
                <textarea value={requestMessage} onChange={e => setRequestMessage(e.target.value)} rows={3}
                  placeholder="Any additional details..."
                  className="w-full border border-[#282828]/12 px-3 py-2.5 text-[13px] bg-white focus:border-[#9A7B4F] focus:outline-none resize-none" data-testid="request-message-input" />
              </div>
            </div>
            <div className="flex gap-3 mt-4">
              <button onClick={() => setShowRequestForm(null)} className="flex-1 py-2.5 text-[11px] text-[#676664] border border-[#282828]/12 tracking-wider uppercase">Cancel</button>
              <button onClick={() => submitRequest(typeKey)} disabled={submitting}
                className="flex-1 py-2.5 bg-[#9A7B4F] text-white text-[11px] tracking-wider uppercase" data-testid="submit-request-btn">
                {submitting ? <Loader2 className="w-4 h-4 animate-spin mx-auto" /> : "Submit Request"}
              </button>
            </div>
          </div>
        ) : (
          <div className="space-y-4">
            {currentSlots.length === 0 ? (
              <div className="text-center py-10 bg-white border border-[#282828]/8">
                <Calendar className="w-8 h-8 text-[#676664]/20 mx-auto mb-3" strokeWidth={1} />
                <p className="text-[13px] text-[#676664]/50">No available slots found</p>
                <button onClick={() => setShowRequestForm(typeKey)} className="mt-3 text-[11px] text-[#9A7B4F] underline" data-testid="request-custom-time-btn">
                  Request a custom time
                </button>
              </div>
            ) : (
              currentSlots.map((dateGroup) => (
                <div key={dateGroup.date} className="bg-white border border-[#282828]/8" data-testid={`slot-date-${dateGroup.date}`}>
                  <div className="px-5 py-3 border-b border-[#282828]/6">
                    <div className="flex items-center gap-2">
                      <Calendar className="w-4 h-4 text-[#9A7B4F]" strokeWidth={1.5} />
                      <span className="text-[13px] text-[#282828] font-medium">{dateGroup.day_label}</span>
                    </div>
                  </div>
                  <div className="p-4">
                    {dateGroup.times.length === 0 ? (
                      <p className="text-[12px] text-[#676664]/50 text-center py-3">All slots booked for this date</p>
                    ) : (
                      <div className="grid grid-cols-3 sm:grid-cols-4 gap-2" data-testid={`time-slots-${dateGroup.date}`}>
                        {dateGroup.times.map((time) => {
                          const isSelected = currentSelection.date === dateGroup.date && currentSelection.time === time;
                          return (
                            <button
                              key={time}
                              onClick={() => setCurrentSelection({ date: dateGroup.date, time })}
                              className={`py-2.5 px-2 text-[12px] border transition-all ${isSelected ? 'bg-[#9A7B4F] text-white border-[#9A7B4F]' : 'bg-white text-[#5A5A5A] border-[#282828]/12 hover:border-[#9A7B4F]/40'}`}
                              data-testid={`time-slot-${time}`}
                            >
                              {formatTime(time)}
                            </button>
                          );
                        })}
                      </div>
                    )}
                  </div>
                </div>
              ))
            )}

            {/* Smart suggestions on conflict */}
            <SuggestionsBlock
              suggestions={stepSuggestions}
              onPick={(time) => {
                // pick first dateGroup or current selection date
                const fallbackDate = currentSelection.date || currentSlots[0]?.date;
                if (fallbackDate) setCurrentSelection({ date: fallbackDate, time });
                setConflictSuggestions((prev) => ({ ...prev, [typeKey]: [] }));
              }}
            />
          </div>
        )}

        {showRequestForm !== typeKey && (
          <div className="mt-4 flex items-start gap-2 text-[11px] text-[#676664]/50">
            <MapPin className="w-3.5 h-3.5 flex-shrink-0 mt-0.5" strokeWidth={1.5} />
            <span>{isPickupStep && isDelivery ? "We'll deliver to your validated address" : "Address will be sent via text message the day before"}</span>
          </div>
        )}

        {showRequestForm !== typeKey && (
          <div className="mt-6 space-y-3">
            <Button
              onClick={() => bookAppointment(typeKey)}
              disabled={submitting || !currentSelection.date || !currentSelection.time || deliveryBlocksConfirm}
              className="w-full bg-[#9A7B4F] hover:bg-[#8B6B3F] text-white py-5 text-[11px] tracking-[0.15em] uppercase disabled:opacity-40"
              data-testid={`book-${typeKey}-btn`}
            >
              {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : `Confirm ${typeLabel} Appointment`}
            </Button>

            <button
              onClick={() => setShowRequestForm(typeKey)}
              className="w-full flex items-center justify-center gap-2 py-3 text-[11px] text-[#9A7B4F]/70 hover:text-[#9A7B4F] tracking-wider transition-colors"
              data-testid={`request-time-${typeKey}-btn`}
            >
              <MessageSquare className="w-3.5 h-3.5" /> Need a different time? Request here
            </button>

            {!isPickupStep && (
              <button onClick={() => setStep("done")} className="w-full text-[11px] text-[#676664]/40 hover:text-[#676664] py-2 tracking-wider" data-testid="skip-dropoff-btn">
                I&apos;ll schedule drop-off later
              </button>
            )}
          </div>
        )}
      </div>
      <Footer />
    </div>
  );
}

function AppointmentCard({ appointment, type, label, onReschedule }) {
  return (
    <div className="bg-white border border-[#282828]/8 p-5" data-testid={`appointment-card-${type}`}>
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          {type === "dropoff" ? (
            <Truck className="w-4 h-4 text-[#9A7B4F]" strokeWidth={1.5} />
          ) : (
            <MapPin className="w-4 h-4 text-[#9A7B4F]" strokeWidth={1.5} />
          )}
          <h3 className="text-[14px] text-[#282828] font-medium">{label || `${type} Appointment`}</h3>
        </div>
        <span className="text-[9px] bg-green-50 text-green-600 px-2 py-0.5 tracking-wider uppercase font-medium">
          {appointment.status}
        </span>
      </div>
      <div className="space-y-2">
        <div className="flex items-center gap-2 text-[13px] text-[#5A5A5A]">
          <Calendar className="w-3.5 h-3.5 text-[#9A7B4F]/50" strokeWidth={1.5} />
          <span>{new Date(appointment.date + "T00:00:00").toLocaleDateString("en-AU", { weekday: "long", day: "numeric", month: "long", year: "numeric" })}</span>
        </div>
        <div className="flex items-center gap-2 text-[13px] text-[#5A5A5A]">
          <Clock className="w-3.5 h-3.5 text-[#9A7B4F]/50" strokeWidth={1.5} />
          <span>{formatTime(appointment.time)}{appointment.duration_minutes ? ` · ${appointment.duration_minutes}m` : ""}</span>
        </div>
        {appointment.delivery_address && (
          <div className="flex items-start gap-2 text-[12px] text-[#5A5A5A]/80">
            <MapPin className="w-3.5 h-3.5 text-[#9A7B4F]/50 mt-0.5" strokeWidth={1.5} />
            <span>{appointment.delivery_address}</span>
          </div>
        )}
      </div>
      <button onClick={onReschedule} className="mt-3 inline-flex items-center gap-1.5 text-[11px] text-[#9A7B4F]/60 hover:text-[#9A7B4F] transition-colors" data-testid={`reschedule-${type}-btn`}>
        <RefreshCw className="w-3 h-3" /> Reschedule
      </button>
    </div>
  );
}
